import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";

import { pricingPlans } from "@/components/landing/pricing-data";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useI18n } from "@/lib/i18n";
import { getLanguageByCode } from "@/lib/languages";
import { buildPlanComparisonPath } from "@/lib/plan-comparison";
import { buildFaqPath } from "@/lib/faq-route";
import { cn } from "@/lib/utils";

function PricingFeatureTooltip({ text, detail, included, tx }) {
  const [isPinned, setIsPinned] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isOpen = isPinned || isTooltipOpen;
  const localizedText = tx(text);
  const localizedDetail = tx(detail);

  return (
    <Tooltip open={isOpen} onOpenChange={setIsTooltipOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${localizedText}: ${localizedDetail}`}
          aria-expanded={isOpen}
          className={cn("landing-pricing__feature-copy", !included && "is-muted")}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsPinned(true);
          }}
        >
          {localizedText}
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        sideOffset={8}
        className="landing-pricing__tooltip !bg-[var(--color-landing-surface)] !text-[var(--color-landing-ink)]"
        onEscapeKeyDown={() => setIsPinned(false)}
        onPointerDownOutside={() => setIsPinned(false)}
      >
        <p>{localizedDetail}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export default function Pricing({ onStart }) {
  const { locale, tx } = useI18n();
  const isRtl = getLanguageByCode(locale).rtl;
  const planComparisonPath = buildPlanComparisonPath(locale);
  const faqPath = buildFaqPath(locale);
  const [viewportRef, emblaApi] = useEmblaCarousel({
    align: "center",
    containScroll: false,
    direction: isRtl ? "rtl" : "ltr",
    loop: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const [isMobileCarousel, setIsMobileCarousel] = useState(() => (
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? true
      : !window.matchMedia("(min-width: 60rem)").matches
  ));

  const syncCarouselState = useCallback((api) => {
    setSelectedIndex(api.selectedScrollSnap());
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }
    emblaApi.scrollTo(0, true);
    syncCarouselState(emblaApi);
    emblaApi.on("select", syncCarouselState);
    emblaApi.on("reInit", syncCarouselState);
    return () => {
      emblaApi.off("select", syncCarouselState);
      emblaApi.off("reInit", syncCarouselState);
    };
  }, [emblaApi, syncCarouselState]);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 60rem)");
    const syncLayoutMode = () => setIsMobileCarousel(!desktopQuery.matches);
    syncLayoutMode();
    desktopQuery.addEventListener("change", syncLayoutMode);
    return () => desktopQuery.removeEventListener("change", syncLayoutMode);
  }, []);

  return (
    <section id="pricing" className="landing-pricing" aria-labelledby="landing-pricing-title">
      <TooltipProvider delayDuration={800}>
        <div className="landing-shell">
          <header className="landing-pricing__heading">
            <h2 id="landing-pricing-title">{tx("Start with 1 free month of the Fun plan")}</h2>
          </header>

          <div
            ref={viewportRef}
            className="landing-pricing__viewport"
            role="region"
            aria-roledescription="carousel"
            aria-label={tx("Pricing plans")}
            data-testid="pricing-carousel"
            tabIndex="0"
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                if (isRtl) {
                  emblaApi?.scrollNext();
                } else {
                  emblaApi?.scrollPrev();
                }
              }
              if (event.key === "ArrowRight") {
                event.preventDefault();
                if (isRtl) {
                  emblaApi?.scrollPrev();
                } else {
                  emblaApi?.scrollNext();
                }
              }
            }}
          >
            <div className="landing-pricing__track">
              {pricingPlans.map((plan, index) => (
                <article
                  key={plan.name}
                  className={cn(
                    "landing-pricing__slide",
                    index === selectedIndex && "is-selected",
                    plan.popular && "is-popular",
                  )}
                  aria-label={`${tx(plan.name)} ${tx("plan")}`}
                  aria-current={index === selectedIndex ? "true" : undefined}
                  inert={isMobileCarousel && index !== selectedIndex ? "" : undefined}
                  data-plan={plan.slug}
                >
                  {plan.popular ? <span className="landing-pricing__badge">{tx("Start here")}</span> : null}
                  {plan.statusLabel ? <span className="landing-pricing__status">{tx(plan.statusLabel)}</span> : null}
                  <div className="landing-pricing__plan-heading">
                    <h3>{tx(plan.name)}</h3>
                    <p>
                      <strong>{plan.price}</strong>
                      <span>{tx(plan.period)}</span>
                    </p>
                  </div>

                  <ul
                    className="landing-pricing__features"
                    tabIndex="0"
                    aria-label={`${tx(plan.name)} ${tx("plan")}`}
                  >
                    {plan.features.map((feature) => (
                      <li key={feature.key}>
                        {feature.included ? (
                          <Check aria-hidden="true" strokeWidth={2.5} />
                        ) : (
                          <X aria-hidden="true" strokeWidth={2} />
                        )}
                        <PricingFeatureTooltip
                          text={feature.text}
                          detail={feature.detail}
                          included={feature.included}
                          tx={tx}
                        />
                      </li>
                    ))}
                  </ul>

                  <Button
                    type="button"
                    className="rounded-full landing-pricing__cta"
                    variant={plan.popular ? "default" : "outline"}
                    disabled={plan.selectable === false}
                    onClick={() => plan.selectable !== false && onStart?.(plan.slug)}
                  >
                    {tx(plan.cta)}
                  </Button>
                </article>
              ))}
            </div>
          </div>

          <div className="landing-pricing__carousel-controls">
            <button
              type="button"
              className="landing-pricing__arrow"
              aria-label={tx("Previous plan")}
              disabled={!canScrollPrev}
              onClick={() => emblaApi?.scrollPrev()}
            >
              {isRtl ? <ChevronRight aria-hidden="true" /> : <ChevronLeft aria-hidden="true" />}
            </button>
            <div className="landing-pricing__dots" aria-label={tx("Choose a pricing plan")}>
              {pricingPlans.map((plan, index) => (
                <button
                  key={plan.slug}
                  type="button"
                  aria-label={tx("Show {plan} plan").replaceAll("{plan}", tx(plan.name))}
                  aria-current={index === selectedIndex ? "true" : undefined}
                  onClick={() => emblaApi?.scrollTo(index)}
                />
              ))}
            </div>
            <button
              type="button"
              className="landing-pricing__arrow"
              aria-label={tx("Next plan")}
              disabled={!canScrollNext}
              onClick={() => emblaApi?.scrollNext()}
            >
              {isRtl ? <ChevronLeft aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
            </button>
          </div>

          <nav className="landing-pricing__links" aria-label={tx("More pricing information")}>
            <Link to={faqPath}>{tx("Go to FAQs")}</Link>
            <Link to={planComparisonPath}>{tx("Compare Plans")}</Link>
          </nav>
        </div>
      </TooltipProvider>
    </section>
  );
}
